<template>
    <ConfigurableImageLandingPage :page-config="pageConfig" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
    createError,
    useRoute,
    useRuntimeConfig,
} from '#imports'
import { useI18n } from '~/composables/useI18n'
import ConfigurableImageLandingPage from '~/components/model/image/ConfigurableImageLandingPage.vue'
import {
    isImageLandingConfig,
    loadLandingPageConfigSource,
    resolveLandingPageConfig,
} from '~/utils/landingPageConfig'
import type {
    ImageLandingConfig,
    LandingPageConfigSource,
} from '~/utils/landingPageConfig'

definePageMeta({
    layout: 'website',
})

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { locale } = useI18n()
const modelConfigs = import.meta.glob('./*.json', {
    eager: true,
    import: 'default',
}) as Record<string, ImageLandingConfig | LandingPageConfigSource<ImageLandingConfig>>

const modelId = computed(() => String(route.params.id || '').trim())
const unavailableImageModelIds = new Set([
    'wan-2-7',
    'wan-2.7',
    'wan-2.7-pro',
    'wan-2-7-pro',
])

if (unavailableImageModelIds.has(modelId.value)) {
    throw createError({
        statusCode: 404,
        statusMessage: `Image model "${modelId.value}" not found`,
    })
}

const configSource = await loadLandingPageConfigSource<ImageLandingConfig>(
    modelId.value,
    modelConfigs,
    runtimeConfig.public.imageModelConfigBaseUrl as string | undefined,
    isImageLandingConfig
)

if (!configSource) {
    throw createError({
        statusCode: 404,
        statusMessage: `Image model config "${modelId.value}" not found`,
    })
}

const pageConfig = computed(() =>
    resolveLandingPageConfig(configSource, locale.value)
)
</script>
